import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Upload, FileText, Download, Trash2, Sparkles, Edit3, Check, X, DollarSign, Building2
} from 'lucide-react';

export default function AdminFinancialTab() {
  const [statements, setStatements] = useState([]);
  const [selectedStmt, setSelectedStmt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [categorizing, setCategorizing] = useState(false);
  const [editingTxn, setEditingTxn] = useState(null);
  const [editNotes, setEditNotes] = useState('');
  const [editCategory, setEditCategory] = useState('');

  const fetchStatements = useCallback(async () => {
    try {
      const res = await api.get('/admin/financial/statements');
      setStatements(res.data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/admin/financial/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(`Uploaded ${res.data.transaction_count} transactions`);
      fetchStatements();
      setSelectedStmt(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload');
    } finally { setUploading(false); }
  };

  const loadStatement = async (stmtId) => {
    try {
      const res = await api.get(`/admin/financial/statements/${stmtId}`);
      setSelectedStmt(res.data);
    } catch { toast.error('Failed to load'); }
  };

  const aiCategorize = async () => {
    if (!selectedStmt) return;
    setCategorizing(true);
    try {
      await api.post(`/admin/financial/statements/${selectedStmt.statement_id}/ai-categorize`);
      toast.success('Transactions categorized by AI');
      loadStatement(selectedStmt.statement_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI categorization failed');
    } finally { setCategorizing(false); }
  };

  const exportCsv = async () => {
    if (!selectedStmt) return;
    try {
      const res = await api.get(`/admin/financial/statements/${selectedStmt.statement_id}/export-csv`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `statement_${selectedStmt.statement_id}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const saveTransaction = async () => {
    if (!selectedStmt || !editingTxn) return;
    try {
      await api.put(
        `/admin/financial/statements/${selectedStmt.statement_id}/transactions/${editingTxn}`,
        { notes: editNotes, category: editCategory }
      );
      toast.success('Transaction updated');
      setEditingTxn(null);
      loadStatement(selectedStmt.statement_id);
    } catch { toast.error('Failed to save'); }
  };

  const toggleBusiness = async (txnId, currentVal) => {
    if (!selectedStmt) return;
    try {
      await api.put(
        `/admin/financial/statements/${selectedStmt.statement_id}/transactions/${txnId}`,
        { is_business: !currentVal }
      );
      loadStatement(selectedStmt.statement_id);
    } catch { toast.error('Failed'); }
  };

  const deleteStatement = async (stmtId) => {
    if (!window.confirm('Delete this statement?')) return;
    try {
      await api.delete(`/admin/financial/statements/${stmtId}`);
      toast.success('Statement deleted');
      if (selectedStmt?.statement_id === stmtId) setSelectedStmt(null);
      fetchStatements();
    } catch { toast.error('Failed'); }
  };

  // Calculate summaries
  const bizTotal = selectedStmt?.transactions?.filter(t => t.is_business).reduce((s, t) => s + t.amount, 0) || 0;
  const personalTotal = selectedStmt?.transactions?.filter(t => !t.is_business).reduce((s, t) => s + t.amount, 0) || 0;
  const categories = {};
  selectedStmt?.transactions?.forEach(t => {
    const cat = t.category || 'Uncategorized';
    if (!categories[cat]) categories[cat] = 0;
    categories[cat] += t.amount;
  });

  return (
    <div data-testid="admin-financial-tab">
      {!selectedStmt ? (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Financial Statements</h2>
              <p className="text-sm text-slate-500">Upload credit card statements for bookkeeping and tax preparation</p>
            </div>
            <label className="cursor-pointer">
              <input type="file" accept=".csv,.pdf" className="hidden" onChange={handleUpload} data-testid="upload-statement-input" />
              <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm text-white transition-all ${uploading ? 'bg-slate-400' : 'bg-[#1E1B4B] hover:bg-[#2D2B66]'}`}>
                <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload CSV / PDF'}
              </div>
            </label>
          </div>

          {statements.length === 0 ? (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="w-16 h-16 text-slate-300 mb-4" />
                <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Statements Yet</p>
                <p className="text-slate-400 mt-2 text-sm">Upload a CSV or PDF credit card statement to get started</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {statements.map(stmt => (
                <Card key={stmt.statement_id} className="rounded-2xl border-slate-100 cursor-pointer hover:shadow-md transition-shadow" onClick={() => loadStatement(stmt.statement_id)} data-testid={`stmt-${stmt.statement_id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#1E1B4B] flex items-center justify-center">
                        <FileText className="w-5 h-5 text-[#C4F017]" />
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-sm">{stmt.filename}</p>
                        <p className="text-xs text-slate-500">{stmt.transaction_count} transactions · ${Math.abs(stmt.total_amount).toFixed(2)} · {new Date(stmt.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); deleteStatement(stmt.statement_id); }}><Trash2 className="w-4 h-4" /></Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Statement Detail View */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <Button variant="ghost" size="sm" className="text-slate-400 mb-1" onClick={() => setSelectedStmt(null)}>← Back to statements</Button>
              <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{selectedStmt.filename}</h2>
              <p className="text-xs text-slate-500">{selectedStmt.transaction_count} transactions · Uploaded {new Date(selectedStmt.uploaded_at).toLocaleDateString()}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={aiCategorize} disabled={categorizing} className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-bold gap-2 text-sm" data-testid="ai-categorize-btn">
                <Sparkles className="w-4 h-4" /> {categorizing ? 'Categorizing...' : 'AI Categorize'}
              </Button>
              <Button onClick={exportCsv} variant="outline" className="rounded-full font-bold gap-2 text-sm border-[#22D3EE] text-[#0E7490]" data-testid="export-csv-btn">
                <Download className="w-4 h-4" /> Export CSV
              </Button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
            <Card className="rounded-2xl border-green-200"><CardContent className="p-4 text-center">
              <Building2 className="w-5 h-5 text-green-600 mx-auto mb-1" />
              <p className="text-xl font-extrabold text-green-700">${Math.abs(bizTotal).toFixed(2)}</p>
              <p className="text-xs text-green-600">Business Expenses</p>
            </CardContent></Card>
            <Card className="rounded-2xl border-slate-200"><CardContent className="p-4 text-center">
              <DollarSign className="w-5 h-5 text-slate-500 mx-auto mb-1" />
              <p className="text-xl font-extrabold text-slate-600">${Math.abs(personalTotal).toFixed(2)}</p>
              <p className="text-xs text-slate-500">Personal</p>
            </CardContent></Card>
            <Card className="rounded-2xl border-[#22D3EE]/30"><CardContent className="p-4 text-center">
              <DollarSign className="w-5 h-5 text-[#0E7490] mx-auto mb-1" />
              <p className="text-xl font-extrabold text-[#0E7490]">${Math.abs(selectedStmt.total_amount).toFixed(2)}</p>
              <p className="text-xs text-[#0E7490]">Grand Total</p>
            </CardContent></Card>
          </div>

          {/* Category Breakdown */}
          {Object.keys(categories).length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {Object.entries(categories).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])).map(([cat, amt]) => (
                <Badge key={cat} variant="outline" className="rounded-full text-xs">{cat}: ${Math.abs(amt).toFixed(2)}</Badge>
              ))}
            </div>
          )}

          {/* Transactions Table */}
          <div className="border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-500">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-500">Description</th>
                    <th className="text-right px-4 py-3 text-xs font-bold text-slate-500">Amount</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-500">Category</th>
                    <th className="text-center px-4 py-3 text-xs font-bold text-slate-500">Business</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-500">Notes</th>
                    <th className="text-center px-4 py-3 text-xs font-bold text-slate-500">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedStmt.transactions.map(txn => (
                    <tr key={txn.transaction_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`txn-${txn.transaction_id}`}>
                      <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">{txn.date}</td>
                      <td className="px-4 py-3 text-xs text-slate-800 max-w-[200px] truncate" title={txn.description}>{txn.description}</td>
                      <td className={`px-4 py-3 text-xs font-bold text-right whitespace-nowrap ${txn.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>${Math.abs(txn.amount).toFixed(2)}</td>
                      <td className="px-4 py-3">
                        {editingTxn === txn.transaction_id ? (
                          <Input className="rounded-lg h-7 text-xs w-28" value={editCategory} onChange={e => setEditCategory(e.target.value)} />
                        ) : (
                          <Badge variant="outline" className="rounded-full text-[10px]">{txn.category || '—'}</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button onClick={() => toggleBusiness(txn.transaction_id, txn.is_business)} className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center mx-auto transition-colors ${txn.is_business ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 bg-white'}`} data-testid={`biz-toggle-${txn.transaction_id}`}>
                          {txn.is_business && <Check className="w-3.5 h-3.5" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        {editingTxn === txn.transaction_id ? (
                          <Input className="rounded-lg h-7 text-xs w-36" value={editNotes} onChange={e => setEditNotes(e.target.value)} placeholder="Add notes..." />
                        ) : (
                          <span className="text-xs text-slate-500">{txn.notes || '—'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {editingTxn === txn.transaction_id ? (
                          <div className="flex gap-1 justify-center">
                            <Button size="sm" className="h-6 w-6 p-0 rounded-lg bg-green-500" onClick={saveTransaction}><Check className="w-3 h-3 text-white" /></Button>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setEditingTxn(null)}><X className="w-3 h-3" /></Button>
                          </div>
                        ) : (
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-400 hover:text-[#0E7490]" onClick={() => { setEditingTxn(txn.transaction_id); setEditNotes(txn.notes || ''); setEditCategory(txn.category || ''); }} data-testid={`edit-txn-${txn.transaction_id}`}><Edit3 className="w-3.5 h-3.5" /></Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
